import { lazy, Suspense } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from '@tanstack/react-router';
import { Box, CircularProgress } from '@mui/material';
import { AppShell } from './components/layout/AppShell';

/**
 * Route components are code-split via React.lazy so each heavy route (Book
 * Editor, Outline, Boards, Story Studio, etc.) ships as its own chunk instead
 * of one monolithic bundle. The root Outlet is wrapped in <Suspense> to render
 * a fallback while a route chunk loads. `named` maps a module's named export to
 * the default shape React.lazy expects.
 */
function named<T extends Record<string, unknown>>(
  loader: () => Promise<T>,
  name: keyof T,
) {
  return lazy(async () => {
    const mod = await loader();
    return { default: mod[name] as React.ComponentType };
  });
}

const DashboardRoute = named(
  () => import('./routes/DashboardRoute'),
  'DashboardRoute',
);
const BooksRoute = named(() => import('./routes/BooksRoute'), 'BooksRoute');
const BookResumeRoute = named(
  () => import('./routes/BookResumeRoute'),
  'BookResumeRoute',
);
const BookEditorRoute = named(
  () => import('./routes/BookEditorRoute'),
  'BookEditorRoute',
);
const ConsistencyRoute = named(
  () => import('./routes/ConsistencyRoute'),
  'ConsistencyRoute',
);
const OutlineRoute = named(
  () => import('./routes/OutlineRoute'),
  'OutlineRoute',
);
const StoryStudioRoute = named(
  () => import('./routes/StoryStudioRoute'),
  'StoryStudioRoute',
);
const BoardsRoute = named(() => import('./routes/BoardsRoute'), 'BoardsRoute');
const MediaLibraryRoute = named(
  () => import('./routes/MediaLibraryRoute'),
  'MediaLibraryRoute',
);
const EntitiesRoute = named(
  () => import('./routes/EntitiesRoute'),
  'EntitiesRoute',
);
const EntityCreateWizardRoute = named(
  () => import('./routes/EntityCreateWizardRoute'),
  'EntityCreateWizardRoute',
);
const EntityVoiceManagementRoute = named(
  () => import('./routes/EntityVoiceManagementRoute'),
  'EntityVoiceManagementRoute',
);
const EntityDetailRoute = named(
  () => import('./routes/EntityDetailRoute'),
  'EntityDetailRoute',
);
const WorldsRoute = named(() => import('./routes/WorldsRoute'), 'WorldsRoute');
const WorldBuilderRoute = named(
  () => import('./routes/WorldBuilderRoute'),
  'WorldBuilderRoute',
);
const CommunityLibraryRoute = named(
  () => import('./routes/SharedLibraryRoute'),
  'CommunityLibraryRoute',
);
const SettingsRoute = named(
  () => import('./routes/SettingsRoute'),
  'SettingsRoute',
);
const StoryArchitectListRoute = named(
  () => import('./routes/StoryArchitectListRoute'),
  'StoryArchitectListRoute',
);
const StoryArchitectDetailRoute = named(
  () => import('./routes/StoryArchitectDetailRoute'),
  'StoryArchitectDetailRoute',
);
const OutlineListRoute = named(
  () => import('./routes/OutlineListRoute'),
  'OutlineListRoute',
);
const OutlineDetailRoute = named(
  () => import('./routes/OutlineDetailRoute'),
  'OutlineDetailRoute',
);
const BoardListRoute = named(
  () => import('./routes/BoardListRoute'),
  'BoardListRoute',
);
const BoardDetailRoute = named(
  () => import('./routes/BoardDetailRoute'),
  'BoardDetailRoute',
);
const StoryStudioListRoute = named(
  () => import('./routes/StoryStudioListRoute'),
  'StoryStudioListRoute',
);
const StoryStudioSessionRoute = named(
  () => import('./routes/StoryStudioSessionRoute'),
  'StoryStudioSessionRoute',
);
const SeriesListRoute = named(
  () => import('./routes/SeriesListRoute'),
  'SeriesListRoute',
);
const SeriesDetailRoute = named(
  () => import('./routes/SeriesDetailRoute'),
  'SeriesDetailRoute',
);
const ReferenceLibraryRoute = named(
  () => import('./routes/ReferenceLibraryRoute'),
  'ReferenceLibraryRoute',
);
const TrainingQueueRoute = named(
  () => import('./routes/TrainingQueueRoute'),
  'TrainingQueueRoute',
);
const ProjectListRoute = named(
  () => import('./routes/ProjectListRoute'),
  'default',
);
const ProjectDashboardRoute = named(
  () => import('./routes/ProjectDashboardRoute'),
  'default',
);
const DebugAiActionsRoute = named(
  () => import('./routes/DebugAiActionsRoute'),
  'DebugAiActionsRoute',
);

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Suspense
        fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        }
      >
        <Outlet />
      </Suspense>
    </AppShell>
  ),
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardRoute,
});
const booksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'books',
  component: BooksRoute,
});
const bookResumeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'books/$bookId/resume',
  component: BookResumeRoute,
});
const bookEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  // genJob carries the chapter-generation jobId across the outline-promote
  // navigation so the editor can show in-progress status immediately. Optional —
  // the editor also recovers a running job by bookId, so other entry points and
  // page reloads work without it.
  validateSearch: (search: Record<string, unknown>) =>
    ({ genJob: (search.genJob as string) ?? undefined }) as { genJob?: string },
  path: 'books/$bookId',
  component: BookEditorRoute,
});
const bookConsistencyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'books/$bookId/consistency',
  component: ConsistencyRoute,
});
const bookOutlineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'books/$bookId/outline',
  component: OutlineRoute,
});
const bookStoryStudioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'books/$bookId/story-studio',
  component: StoryStudioRoute,
});
const bookBoardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'books/$bookId/boards',
  component: BoardsRoute,
});
const imagesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'images',
  component: MediaLibraryRoute,
});
const entitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'entities',
  component: EntitiesRoute,
});
const entityCreateWizardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'entities/new',
  component: EntityCreateWizardRoute,
});
const entityVoiceManagementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'entities/voice-management',
  component: EntityVoiceManagementRoute,
});
const entityDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'entities/$entityId',
  component: EntityDetailRoute,
});
const worldsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'worlds',
  component: WorldsRoute,
});
const worldBuilderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'worlds/$worldId',
  component: WorldBuilderRoute,
});
const sharedLibraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'libraries/shared',
  component: CommunityLibraryRoute,
});
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'settings',
  component: SettingsRoute,
});
const storyArchitectListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'story-architect',
  component: StoryArchitectListRoute,
});
const storyArchitectDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'story-architect/$sessionId',
  component: StoryArchitectDetailRoute,
});
const outlineListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'outlines',
  component: OutlineListRoute,
});
const outlineDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'outlines/$outlineId',
  component: OutlineDetailRoute,
  validateSearch: (search: Record<string, unknown>) =>
    ({
      nodeId: (search.nodeId as string) || undefined,
    }) as { nodeId?: string },
});
const boardListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'boards',
  component: BoardListRoute,
});
const boardDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'boards/$boardId',
  component: BoardDetailRoute,
});
const storyStudioListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'story-studio',
  component: StoryStudioListRoute,
});
const storyStudioSessionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'story-studio/$sessionId',
  component: StoryStudioSessionRoute,
  validateSearch: (search: Record<string, unknown>) =>
    ({
      mode: search.mode === 'draft' ? 'draft' : undefined,
    }) as { mode?: 'draft' },
});
const seriesListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'series',
  component: SeriesListRoute,
});
const seriesDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'series/$seriesId',
  component: SeriesDetailRoute,
});
const referenceLibraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'reference-library',
  component: ReferenceLibraryRoute,
});
const trainingQueueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'training-queue',
  component: TrainingQueueRoute,
});
const projectListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'projects',
  component: ProjectListRoute,
});
const projectDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'projects/$projectId',
  component: ProjectDashboardRoute,
});
const debugAiActionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'debug/ai-actions',
  component: DebugAiActionsRoute,
});

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  booksRoute,
  bookResumeRoute,
  bookEditorRoute,
  bookConsistencyRoute,
  bookOutlineRoute,
  bookStoryStudioRoute,
  bookBoardsRoute,
  imagesRoute,
  entitiesRoute,
  entityCreateWizardRoute,
  entityVoiceManagementRoute,
  entityDetailRoute,
  worldsRoute,
  worldBuilderRoute,
  sharedLibraryRoute,
  storyArchitectListRoute,
  storyArchitectDetailRoute,
  outlineListRoute,
  outlineDetailRoute,
  boardListRoute,
  boardDetailRoute,
  storyStudioListRoute,
  storyStudioSessionRoute,
  seriesListRoute,
  seriesDetailRoute,
  referenceLibraryRoute,
  trainingQueueRoute,
  projectListRoute,
  projectDashboardRoute,
  settingsRoute,
  debugAiActionsRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
